import { useContext, useState, useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { MaterialContext } from "../context/MaterialContext";
import Swatch from "../components/Swatch";
import { pdfIcon, starOutlineIcon } from "../components/Icons";
import { isVendorUser } from "../utilities/AuthState";
import { isDesignerHiddenStatus } from "../utilities/MaterialDetails";
import { trackEvent } from "../utilities/analytics";
import { getGridColumnCount, remainingGridSpan } from "../utilities/gridColumns";

function Collection() {
  const { allMaterials } = useContext(MaterialContext);
  const { collection, updateCollection, userInformation } = useContext(UserContext);
  const navigate = useNavigate();
  const [checkedCollection, setCheckedCollection] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showViewed, setShowViewed] = useState(false);
  const [viewedMaterials, setViewedMaterials] = useState(false);
  const [actionSpan, setActionSpan] = useState(1);
  const gridRef = useRef(null);

  const haveCollection = collection.length >= 1;

  // Mount-only: capture the collection size when this page opens.
  useEffect(() => {
    trackEvent("collection_view", { count: collection.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClearViewedMaterials = () => {
    localStorage.removeItem("viewedMats");
    setViewedMaterials(false);
    setShowViewed(false);
    setCheckedCollection([]);
  };

  useEffect(() => {
    const isExist = allMaterials
      .filter(({ id }) => collection.includes(id))
      .map((item) => item);
    const visibleMaterials = isExist.filter(
      (item) =>
        isVendorUser(userInformation) || !isDesignerHiddenStatus(item.status),
    );
    if (isExist.length === collection.length) {
      setCheckedCollection(visibleMaterials);
      setLoaded(true);
    } else {
      if (collection.length >= 1 && collection.length !== isExist.length) {
        const notExist = collection
          .filter((item) => isExist.includes(item))
          .map((item) => item);
        setCheckedCollection(
          visibleMaterials.filter((item) => notExist.includes(item)),
        );
        setLoaded(true);
        updateCollection(notExist);
      }
    }
    if (!haveCollection) {
      const storedViewedMats = localStorage.getItem("viewedMats");
      if (storedViewedMats) {
        try {
          const viewedMats = JSON.parse(storedViewedMats);
          if (Array.isArray(viewedMats) && viewedMats.length) {
            const viewedMaterialsList = allMaterials
              .filter(
                ({ id, status }) =>
                  viewedMats.includes(id) &&
                  (isVendorUser(userInformation) ||
                    !isDesignerHiddenStatus(status)),
              )
              .map((item) => item);
            setCheckedCollection(viewedMaterialsList);
            setViewedMaterials(true);
            setLoaded(true);
          }
        } catch {
          localStorage.removeItem("viewedMats");
        }
      }
    }
  }, [collection, allMaterials, updateCollection, haveCollection, userInformation]);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) {
      return undefined;
    }

    const updateSpan = () => {
      setActionSpan(
        remainingGridSpan(checkedCollection.length, getGridColumnCount(grid)),
      );
    };

    updateSpan();

    const observer = new ResizeObserver(updateSpan);
    observer.observe(grid);
    window.addEventListener("resize", updateSpan);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateSpan);
    };
  }, [checkedCollection.length, loaded, showViewed, haveCollection]);

  const actionPanelStyle = {
    gridColumn: `span ${actionSpan}`,
  };

  if (loaded && collection.length >= 1) {
    return (
      <div className="content">
        <div className="swatch-view" ref={gridRef}>
          {checkedCollection.map((item) => (
            <Swatch key={item.id} id={item.id} />
          ))}
          <div
            className="collection-action-panel swatch-view-tile"
            style={actionPanelStyle}
          >
            <div className="collection-action-glow" aria-hidden="true" />
            <div className="collection-action-inner">
              <div className="collection-action-icon" aria-hidden="true">
                {pdfIcon}
              </div>
              <p className="collection-action-eyebrow">Collection</p>
              <h3 className="collection-action-title">Ready to share</h3>
              <p className="collection-action-copy">
                {checkedCollection.length}{" "}
                {checkedCollection.length === 1 ? "material" : "materials"} saved for
                review or export.
              </p>
              <div className="collection-action-buttons">
                <button
                  type="button"
                  className="collection-action-primary"
                  onClick={() => navigate("/collection/pdf")}
                >
                  Export to PDF
                </button>
                <button
                  type="button"
                  className="collection-action-secondary"
                  onClick={() => updateCollection([])}
                >
                  Clear collection
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      {showViewed ? (
        <div className="swatch-view" ref={gridRef}>
          {checkedCollection.map((item) => (
            <Swatch key={item.id} id={item.id} />
          ))}
          <div
            className="collection-action-panel swatch-view-tile"
            style={actionPanelStyle}
          >
            <div className="collection-action-glow" aria-hidden="true" />
            <div className="collection-action-inner">
              <div className="collection-action-icon" aria-hidden="true">
                {starOutlineIcon}
              </div>
              <p className="collection-action-eyebrow">Recently viewed</p>
              <h3 className="collection-action-title">Viewed materials</h3>
              <p className="collection-action-copy">
                {checkedCollection.length}{" "}
                {checkedCollection.length === 1 ? "swatch" : "swatches"} from
                this session.
              </p>
              <div className="collection-action-buttons">
                {viewedMaterials ? (
                  <button
                    type="button"
                    className="collection-action-primary"
                    onClick={() => setShowViewed(!showViewed)}
                  >
                    {showViewed
                      ? `Hide ${checkedCollection.length} viewed`
                      : `Show ${checkedCollection.length} viewed`}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="collection-action-secondary"
                  onClick={handleClearViewedMaterials}
                >
                  Clear viewed
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <div className="empty-collection">
        <div className="empty-collection-message empty-collection-card">
          <div className="empty-collection-glow" aria-hidden="true" />
          <div className="text-box">
            <div className="empty-collection-icon" aria-hidden="true">
              {starOutlineIcon}
            </div>
            <p className="empty-collection-eyebrow">Collection</p>
            <h2>Your collection is empty</h2>
            <h4>
              Tap the star on any swatch in the library to save it here for
              review, sharing, or PDF export.
            </h4>
            <div className="empty-collection-actions">
              <button
                type="button"
                className="empty-collection-primary"
                onClick={() => navigate("/library")}
              >
                Browse library
              </button>
              {viewedMaterials ? (
                <button
                  type="button"
                  className="empty-collection-secondary"
                  onClick={() => setShowViewed(!showViewed)}
                >
                  {showViewed
                    ? `Hide ${checkedCollection.length} viewed materials`
                    : `Show ${checkedCollection.length} viewed materials`}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Collection;
